import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Search, Brain, ClipboardCheck, Handshake } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';

const Services = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const valuePoints = [
    'Success-based recruitment with no upfront financial risk',
    'Deep candidate evaluation focused on long-term fit',
    'Access to passive, high-caliber professionals',
    'Market insight to strengthen hiring decisions',
    'Reduced hiring timelines through proactive pipeline development',
    '90-day placement guarantee for added confidence'
  ];

  const approachSteps = [
    {
      icon: Search,
      title: 'Discovery & Strategic Alignment',
      description: 'We begin with a structured consultation to understand not only the role, but the broader business context — growth plans, leadership expectations, and workforce challenges.'
    },
    {
      icon: Brain,
      title: 'Talent Mapping & Market Intelligence',
      description: 'Rather than waiting for applicants, we proactively identify and engage professionals aligned with your needs. This builds a curated pipeline that supports both current and future hiring initiatives.'
    },
    {
      icon: ClipboardCheck,
      title: 'Evaluation & Candidate Presentation',
      description: "Candidates are assessed for technical capability, communication style, and long-term organizational fit. You receive a focused shortlist — not an overwhelming volume of resumes."
    },
    {
      icon: Handshake,
      title: 'Interview Partnership & Placement Support',
      description: 'From interview coordination to offer guidance and onboarding insight, we remain actively involved to ensure a smooth hiring experience and strong retention outcomes.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Recruiting Services"
        description="Direct hire recruiting and staffing services from Omnia Paratus Talent Solutions. Strategic recruitment partnerships for accounting, healthcare, logistics, and HR & people compliance industries. Serving Massachusetts and nationwide."
        keywords="direct hire recruiting, staffing services, recruitment agency services, contingent recruiting, talent pipeline development, executive search services, professional staffing"
      />
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1F44] to-[#0d2b5c]">
        <div className="max-w-7xl mx-auto text-center">
          <h1 data-testid="services-hero-title" className="text-5xl md:text-6xl font-bold text-white">
            Our Services
          </h1>
        </div>
      </section>

      {/* Strategic Recruitment Partnership */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 data-testid="services-partnership-title" className="text-3xl md:text-4xl font-bold text-[#0A1F44] mb-3 text-center">
            Strategic Recruitment Partnership
          </h2>
          <p className="text-lg text-[#D4AF37] font-medium text-center mb-10">
            Precision hiring supported by proactive talent intelligence
          </p>

          <div className="space-y-6 text-gray-700 text-lg leading-relaxed">
            <p>
              At Omnia Paratus Talent Solutions, recruitment is not a reactive transaction — it's a structured strategy. We combine contingent direct hire recruiting with forward-looking talent pipeline development, giving organizations both immediate hiring results and long-term workforce readiness.
            </p>
            <p>
              Our success-based model means there are no retainers or upfront costs. Instead, we operate as a strategic partner, aligning every search with your business goals, culture, and future growth plans.
            </p>
          </div>
        </div>
      </section>

      {/* The Value We Deliver */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 data-testid="services-value-title" className="text-3xl md:text-4xl font-bold text-[#0A1F44] mb-10 text-center">
            The Value We Deliver
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
            {valuePoints.map((point, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 text-lg">{point}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 data-testid="services-approach-title" className="text-3xl md:text-4xl font-bold text-[#0A1F44] mb-14 text-center">
            Our Approach
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {approachSteps.map((step, idx) => {
              const IconComp = step.icon;
              return (
                <Card key={idx} data-testid={`approach-step-${idx + 1}`} className="border-2 border-gray-200 hover:border-[#D4AF37] transition-all duration-300 hover:shadow-xl">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-[#0A1F44] rounded-xl flex items-center justify-center mb-5 group-hover:bg-[#D4AF37] transition-colors duration-300">
                      <IconComp className="h-7 w-7 text-[#D4AF37]" />
                    </div>
                    <h3 className="text-xl font-bold text-[#0A1F44] mb-3">{step.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{step.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1F44] to-[#0d2b5c]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-lg text-gray-200 mb-10">
            Let's discuss how we can support your next hire — and every one after that.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              asChild
              size="lg"
              className="bg-[#D4AF37] text-[#0A1F44] hover:bg-[#c9a432] text-lg px-8 py-6 shadow-xl font-semibold"
            >
              <Link to="/contact?scrollTop=true" className="flex items-center gap-2">
                Start the Conversation <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-white/30 text-white hover:bg-white hover:text-[#0A1F44] text-lg px-8 py-6"
            >
              <Link to="/employers#hiring-process">How We Deliver Results</Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;
